/**
 * ============================================================================
 * COMPOSABLE: Отслеживание активности пользователя
 * ============================================================================
 * Real-time отслеживание активности пользователя для обновления статуса "онлайн"
 * Аналогично системе "был(а) в сети" в Telegram
 * ============================================================================
 */

import { ref, onMounted, onUnmounted } from 'vue'
import axios from 'axios'

/**
 * Константы для настройки отслеживания
 */
const HEARTBEAT_INTERVAL = 2 * 60 * 1000 // Отправка heartbeat каждые 2 минуты
const DEBOUNCE_DELAY = 5 * 1000 // Задержка перед отправкой heartbeat после активности: 5 секунд
const EVENTS_TO_TRACK = [
	'click',
	'keydown',
	'scroll',
	'mousemove',
	'touchstart',
	'focus',
]

/**
 * Composable для отслеживания активности пользователя
 * @param {Object} options - Опции конфигурации
 * @param {boolean} options.enabled - Включить/выключить отслеживание (по умолчанию true)
 * @param {number} options.heartbeatInterval - Интервал отправки heartbeat в мс
 * @param {number} options.debounceDelay - Задержка debounce в мс
 * @returns {Object} - Объект с методами управления трекером
 */
export function useActivityTracker(options = {}) {
	const {
		enabled = true,
		heartbeatInterval = HEARTBEAT_INTERVAL,
		debounceDelay = DEBOUNCE_DELAY,
	} = options

	// Состояние
	const isTracking = ref(false)
	const lastActivityTime = ref(null)
	const lastHeartbeatTime = ref(null)

	// Таймеры
	let debounceTimer = null
	let heartbeatTimer = null
	let eventListeners = []

	function getAuthToken() {
		const tokenCamel = localStorage.getItem('accessToken')
		const tokenSnake = localStorage.getItem('access_token')
		const token = tokenCamel || tokenSnake || null

		if (!token) return null

		try {
			const tokenParts = token.split('.')
			if (tokenParts.length !== 3) {
				return null
			}

			const payload = JSON.parse(atob(tokenParts[1]))
			const expiresAt = Number(payload?.exp) * 1000

			if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
				return null
			}

			return token
		} catch (_) {
			return null
		}
	}

	/**
	 * Отправка heartbeat на сервер
	 */
	async function sendHeartbeat() {
		try {
			const token = getAuthToken()
			
			if (!token) {
				console.debug('[ActivityTracker] ��� ������ - ���������� heartbeat')
				stopTracking()
				return
			}

			await axios.post(
				'/api/v1/activity/heartbeat',
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			)

			lastHeartbeatTime.value = new Date()
			console.debug('[ActivityTracker] Heartbeat отправлен успешно')
		} catch (error) {
			// Не критичная ошибка - просто логируем
			if (error.response?.status === 429) {
				console.warn('[ActivityTracker] Rate limit превышен для heartbeat')
			} else if (error.response?.status === 401) {
				console.debug('[ActivityTracker] Пользователь не авторизован')
				stopTracking() // Останавливаем отслеживание если не авторизован
			} else {
				console.error('[ActivityTracker] Ошибка отправки heartbeat:', error.message)
			}
		}
	}

	/**
	 * Обработчик активности пользователя
	 * Использует debounce для предотвращения слишком частых вызовов
	 */
	function handleActivity() {
		lastActivityTime.value = new Date()

		// Очищаем предыдущий таймер debounce
		if (debounceTimer) {
			clearTimeout(debounceTimer)
		}

		// Устанавливаем новый таймер debounce
		debounceTimer = setTimeout(() => {
			// Проверяем, прошло ли достаточно времени с последнего heartbeat
			const now = Date.now()
			const timeSinceLastHeartbeat = lastHeartbeatTime.value
				? now - lastHeartbeatTime.value.getTime()
				: Infinity

			// Отправляем heartbeat только если прошло достаточно времени
			if (timeSinceLastHeartbeat >= heartbeatInterval) {
				sendHeartbeat()
			}
		}, debounceDelay)
	}

	/**
	 * Периодическая отправка heartbeat (fallback)
	 * Отправляет heartbeat даже если нет активности, но пользователь онлайн
	 */
	function startHeartbeatTimer() {
		heartbeatTimer = setInterval(() => {
			// Отправляем heartbeat только если была недавняя активность
			const now = Date.now()
			const timeSinceActivity = lastActivityTime.value
				? now - lastActivityTime.value.getTime()
				: Infinity

			// Если активность была в последние 5 минут - отправляем heartbeat
			if (timeSinceActivity < 5 * 60 * 1000) {
				sendHeartbeat()
			}
		}, heartbeatInterval)
	}

	/**
	 * Добавление слушателей событий
	 */
	function addEventListeners() {
		EVENTS_TO_TRACK.forEach(eventType => {
			const handler = handleActivity
			
			// Для mousemove и scroll используем throttle через passive listener
			const options = ['mousemove', 'scroll'].includes(eventType)
				? { passive: true, capture: false }
				: false

			window.addEventListener(eventType, handler, options)
			
			eventListeners.push({
				type: eventType,
				handler,
				options,
			})
		})

		console.debug('[ActivityTracker] Event listeners добавлены')
	}

	/**
	 * Удаление слушателей событий
	 */
	function removeEventListeners() {
		eventListeners.forEach(({ type, handler, options }) => {
			window.removeEventListener(type, handler, options)
		})
		eventListeners = []
		console.debug('[ActivityTracker] Event listeners удалены')
	}

	/**
	 * Запуск отслеживания активности
	 */
	function startTracking() {
		if (isTracking.value) {
			console.debug('[ActivityTracker] Отслеживание уже запущено')
			return
		}

		if (!enabled) {
			console.debug('[ActivityTracker] Отслеживание отключено в настройках')
			return
		}

		// Проверяем авторизацию
		const token = getAuthToken()
		if (!token) {
				console.debug('[ActivityTracker] ��� ������ - ���������� heartbeat')
				stopTracking()
				return
			}

		isTracking.value = true
		lastActivityTime.value = new Date()

		// Отправляем первый heartbeat сразу
		sendHeartbeat()

		// Добавляем слушатели событий
		addEventListeners()

		// Запускаем периодический heartbeat
		startHeartbeatTimer()

		console.info('[ActivityTracker] Отслеживание активности запущено')
	}

	/**
	 * Остановка отслеживания активности
	 */
	function stopTracking() {
		if (!isTracking.value) {
			return
		}

		isTracking.value = false

		// Удаляем слушатели событий
		removeEventListeners()

		// Очищаем таймеры
		if (debounceTimer) {
			clearTimeout(debounceTimer)
			debounceTimer = null
		}

		if (heartbeatTimer) {
			clearInterval(heartbeatTimer)
			heartbeatTimer = null
		}

		console.info('[ActivityTracker] Отслеживание активности остановлено')
	}

	/**
	 * Принудительная отправка heartbeat
	 */
	function forceHeartbeat() {
		sendHeartbeat()
	}

	// Автоматический запуск при монтировании компонента
	onMounted(() => {
		if (enabled) {
			startTracking()
		}
		window.addEventListener('auth:login', startTracking)
		window.addEventListener('auth:logout', stopTracking)
	})

	// Автоматическая остановка при размонтировании компонента
	onUnmounted(() => {
		window.removeEventListener('auth:login', startTracking)
		window.removeEventListener('auth:logout', stopTracking)
		stopTracking()
	})

	// Возвращаем API
	return {
		isTracking,
		lastActivityTime,
		lastHeartbeatTime,
		startTracking,
		stopTracking,
		forceHeartbeat,
	}
}

